import { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import ResultCard from '../ResultCard';
import { ReportCardResult } from '../../types/reports';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';

interface SliderProps {
  reportsDataWithScores: ReportCardResult[];
  activeReportDateCentreReportId: number;
  onClickCardResults: () => void;
}

const TestResultCardSlider = ({
  reportsDataWithScores,
  activeReportDateCentreReportId,
  onClickCardResults,
}: SliderProps) => {
  const { theme } = useThemeContext();
  const findActiveIndex = reportsDataWithScores.findIndex(
    (report) => report.centre_report_id === activeReportDateCentreReportId,
  );
  const [activeIndex, setActiveIndex] = useState(findActiveIndex);
  const navigate = useNavigate();
  const onlyOneResult = reportsDataWithScores.length === 1;

  const navigateToReport = (currentIndex: number) => {
    const reportDetails = reportsDataWithScores[currentIndex];
    const reportUrl = `/report/${reportDetails.centre_report_id}`;
    navigate(reportUrl);
  };

  const handlePrevious = () => {
    setActiveIndex((prev) => {
      const previousPosition = Math.max(0, prev - 1);
      navigateToReport(previousPosition);
      return previousPosition;
    });
  };

  const handleNext = () => {
    setActiveIndex((prev) => {
      const nextPosition = Math.min(reportsDataWithScores.length - 1, prev + 1);
      navigateToReport(nextPosition);
      return nextPosition;
    });
  };

  const getMarginLeftMainCard = () => {
    if (onlyOneResult) return '0px';
    if (activeIndex === 0) return '35%';
    if (activeIndex === reportsDataWithScores.length - 1) return '63%';

    return '50%';
  };

  const getWidthMainCard = () => {
    if (onlyOneResult) return '100%';
    if (activeIndex === 0) return '70%';
    if (activeIndex === reportsDataWithScores.length - 1) return '73%';

    return '78%';
  };

  const getMarginRightPreviousCard = () => {
    if (activeIndex === reportsDataWithScores.length - 1) return '311px';

    return '385px';
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      {/* Navigation Buttons */}
      {activeIndex > 0 && (
        <IconButton
          disableRipple
          onClick={handlePrevious}
          sx={{
            color: 'white',
            backgroundColor: 'transparent',
          }}
        >
          <Box component="img" src={theme.images.navigateBackIcon.src} width="17px" />
        </IconButton>
      )}
      {/* Cards Container */}
      <Box
        sx={{
          width: '100%',
          height: '138px',
          position: 'relative',
          overflow: 'hidden',
          px: onlyOneResult ? '0px !important' : 2,
        }}
      >
        {/* Previous Card - positioned left of main card */}
        {activeIndex > 0 && (
          <Box
            sx={{
              position: 'absolute',
              right: getMarginRightPreviousCard(),
              width: '92%',
              height: '100%',
              marginTop: '17px',
              opacity: 0.5,
              transition: 'all 0.3s ease',
            }}
          >
            <ResultCard
              result={reportsDataWithScores[activeIndex - 1]}
              additionaStyles={{
                width: '390px',
                height: '105px',
                border: `1px solid ${theme.custom.report.details.resultCard.slider.previousCardBorderColor}`,
                padding: '5px 10px',
                gap: '0px',
              }}
              onClick={onClickCardResults}
            />
          </Box>
        )}
        {/* Main card */}
        <Box
          sx={{
            // when only one result available
            position: onlyOneResult ? 'relative' : 'absolute',
            left: getMarginLeftMainCard(),
            transform: onlyOneResult ? '0px' : 'translateX(-50%)',
            width: getWidthMainCard(),
            height: '100%',
            transition: 'all 0.3s ease',
          }}
        >
          <ResultCard
            result={reportsDataWithScores[activeIndex]}
            additionaStyles={{
              width: '100%',
              height: '100%',
              border: `2px solid ${theme.custom.report.details.resultCard.slider.currentCardBorderColor}`,
              padding: '16px',
              gap: '8px',
            }}
            onClick={onClickCardResults}
          />
        </Box>
        {/* Next Card - positioned right of main card */}
        {activeIndex < reportsDataWithScores.length - 1 && (
          <Box
            sx={{
              position: 'absolute',
              right: activeIndex === 0 ? '-65%' : '-83%',
              width: '92%',
              height: '100%',
              opacity: 0.5,
              transition: 'all 0.3s ease',
            }}
          >
            <ResultCard
              result={reportsDataWithScores[activeIndex + 1]}
              additionaStyles={{
                width: '390px',
                height: '105px',
                border: `1px solid ${theme.custom.report.details.resultCard.slider.nextCardBorderColor}`,
                marginTop: '17px',
                padding: '5px 10px',
                gap: '0px',
              }}
              onClick={onClickCardResults}
            />
          </Box>
        )}
      </Box>
      {activeIndex < reportsDataWithScores.length - 1 && (
        <IconButton
          disableRipple
          onClick={handleNext}
          sx={{
            color: 'white',
            backgroundColor: 'transparent',
          }}
        >
          <Box component="img" src={theme.images.navigateNextIcon.src} width="17px" />
        </IconButton>
      )}
    </Box>
  );
};

export default TestResultCardSlider;
